import { Link } from "react-router-dom";
import { Globe2, Users, Handshake, ArrowRight, Sparkles, Mail } from "lucide-react";
import useSEO from "../hooks/useSEO";
import usePageContent from "../hooks/usePageContent";
import PageContent from "../components/PageContent";

export default function EspaceCeda() {
  useSEO({
    title: "Espace CEDA — Club Entrepreneurial Diaspo-Afrique",
    description: "Le CEDA, créé par Demka Services Inc., est un réseau d'entrepreneurs de la diaspora africaine engagés pour l'Afrique. Un espace de rencontres, de collaboration et d'opportunités d'affaires.",
  });
  const intro = usePageContent("ceda.intro", "Le Club Entrepreneurial Diaspo-Afrique (CEDA), initiative de Demka Services Inc., est un réseau d'entrepreneurs de la diaspora africaine engagés dans la construction de ponts commerciaux entre le Canada et l'Afrique.");
  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-14 sm:py-20" data-testid="ceda-page">
      {/* Hero */}
      <div className="text-center max-w-3xl mx-auto">
        <div className="inline-flex items-center justify-center rounded-full bg-white shadow-sm border border-primary/10 p-3 mb-6" data-testid="ceda-logo">
          <img src="/media/logos/ceda.jpg" alt="Logo du Club Entrepreneurial Diaspo-Afrique (CEDA)" className="w-24 h-24 object-contain" />
        </div>
        <p className="text-xs uppercase tracking-widest text-accent font-semibold mb-2">Espace CEDA</p>
        <h1 className="font-display font-bold text-4xl sm:text-5xl lg:text-6xl text-foreground tracking-tight leading-[1.05]">
          Connecter <span className="text-accent italic">l'Afrique</span> au{" "}
          <span className="text-primary">Canada</span>.
        </h1>
        <p className="text-muted-foreground mt-6 leading-relaxed max-w-2xl mx-auto" data-testid="ceda-intro-text">
          <PageContent value={intro} />
        </p>
      </div>

      {/* Description */}
      <section className="mt-14 grid lg:grid-cols-2 gap-10 items-center">
        <div className="space-y-4 text-foreground/85 leading-relaxed">
          <h2 className="font-display font-bold text-2xl sm:text-3xl">Un espace, plusieurs opportunités</h2>
          <p>
            Le CEDA est un <strong>espace dynamique de rencontres, de collaboration, de formation et de recherche
            d'opportunités d'affaires</strong>, destiné aux entrepreneurs issus de la diaspora africaine, au Canada
            et à partir du Québec.
          </p>
          <p>
            À travers cette initiative, DemkaBiz annonce l'ouverture progressive de sa plateforme vers l'Afrique, pour
            faciliter les échanges commerciaux, les partenariats stratégiques et la circulation des expertises entre les
            deux rives.
          </p>
          <p className="italic text-primary font-display">
            « DemkaBiz vous accompagnera en Afrique pour y développer votre marché. »
          </p>
        </div>
        <div className="rounded-3xl overflow-hidden bg-white border border-primary/10 aspect-square flex items-center justify-center p-10">
          <img
            src="/media/logos/ceda.jpg"
            alt="Logo CEDA — Club Entrepreneurial Diaspo-Afrique"
            className="max-w-full max-h-full object-contain"
          />
        </div>
      </section>

      {/* What we offer */}
      <section className="mt-16">
        <h2 className="font-display font-bold text-2xl sm:text-3xl text-center">Ce que le CEDA propose</h2>
        <div className="grid md:grid-cols-3 gap-5 mt-8">
          <div className="card-surface p-6" data-testid="ceda-feature-network">
            <Users className="w-7 h-7 text-primary mb-3" />
            <h3 className="font-display font-semibold text-lg">Un réseau engagé</h3>
            <p className="text-sm text-muted-foreground mt-2 leading-relaxed">
              Des rencontres régulières entre entrepreneurs, mentors et partenaires institutionnels des deux rives.
            </p>
          </div>
          <div className="card-surface p-6" data-testid="ceda-feature-training">
            <Sparkles className="w-7 h-7 text-accent mb-3" />
            <h3 className="font-display font-semibold text-lg">Formation & inspiration</h3>
            <p className="text-sm text-muted-foreground mt-2 leading-relaxed">
              Ateliers, webinaires et conférences pour outiller les porteurs de projets Canada-Afrique.
            </p>
          </div>
          <div className="card-surface p-6" data-testid="ceda-feature-collab">
            <Handshake className="w-7 h-7 text-primary mb-3" />
            <h3 className="font-display font-semibold text-lg">Collaboration & marchés</h3>
            <p className="text-sm text-muted-foreground mt-2 leading-relaxed">
              Recherche d'opportunités d'affaires concrètes et mise en relation avec des marchés en Afrique.
            </p>
          </div>
        </div>
      </section>

      {/* CTA band — contact */}
      <section className="mt-16 rounded-3xl bg-primary text-primary-foreground p-8 sm:p-12 grid lg:grid-cols-[1fr_auto] items-center gap-6" data-testid="ceda-cta-band">
        <div>
          <div className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-white/85 font-semibold mb-3">
            <Globe2 className="w-4 h-4 text-accent" />
            Contactez-nous
          </div>
          <h2 className="font-display font-bold text-2xl sm:text-3xl leading-tight">
            DemkaBiz vous accompagne en Afrique pour<br />y développer votre marché.
          </h2>
          <p className="text-white/85 mt-3 leading-relaxed max-w-2xl">
            Vous portez un projet Canada-Afrique ? Écrivez-nous pour rejoindre le CEDA et bénéficier de notre accompagnement.
          </p>
        </div>
        <div className="flex flex-col sm:flex-row gap-3 flex-shrink-0">
          <a
            href="mailto:contact@demka.ca"
            data-testid="ceda-cta-email"
            className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full bg-accent text-white hover:bg-accent/90 transition font-medium"
          >
            <Mail className="w-4 h-4" />
            contact@demka.ca
          </a>
          <Link
            to="/contact"
            data-testid="ceda-cta-contact"
            className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full bg-white/10 hover:bg-white/20 border border-white/25 transition font-medium"
          >
            Formulaire de contact <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      <p className="text-center text-xs text-muted-foreground mt-8">
        Une initiative de <strong className="text-foreground">Demka Services Inc.</strong>
      </p>
    </div>
  );
}
